import { useCallback, useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';
import {
  formatMissionCoefficient,
  parseMissionCoefficient,
} from 'helper/missionCoefficient';

const IDLE = { status: 'idle', companyValue: null };

// The wage comes from the job description as text ('12.5'); anything that is
// not a positive amount leaves the company value unknown, without a request.
export const parseWagePerHour = (salary) => {
  if (typeof salary === 'number') return salary > 0 ? salary : null;
  if (typeof salary !== 'string' || salary.trim() === '') return null;
  const wage = Number(salary.trim().replace(',', '.'));
  return Number.isFinite(wage) && wage > 0 ? wage : null;
};

const readCompanyCoefficient = (answer) => {
  const value = answer?.data?.coefficient;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * The billing coefficient of the mission being created (JBWEB-1201): the
 * value the company grid gives for its wage, and the one the admin may type
 * instead. `override` is null as long as the admin has not changed the value:
 * typing the company value back, clearing the field or resetting all mean the
 * mission follows the grid.
 */
const useMissionCoefficientField = (idCompany, salary) => {
  const wagePerHour = parseWagePerHour(salary);
  const [request, setRequest] = useState(IDLE);
  const [isEditing, setIsEditing] = useState(false);
  const [input, setInput] = useState('');
  // Only the latest request may fill the field.
  const latestRequest = useRef(null);

  const reset = useCallback(() => {
    setIsEditing(false);
    setInput('');
  }, []);

  // Another company or wage: the value typed for the former one is dropped,
  // the admin decides again with the new company value in front of them.
  useEffect(() => {
    reset();
    if (!idCompany || wagePerHour === null) {
      latestRequest.current = null;
      setRequest(IDLE);
      return undefined;
    }
    const current = {};
    latestRequest.current = current;
    setRequest({ status: 'loading', companyValue: null });
    ApiDatabase.getCompanyCoefficient(
      { idCompany, wagePerHour },
      (answer) => {
        if (latestRequest.current !== current) return;
        setRequest({
          status: 'ready',
          companyValue: readCompanyCoefficient(answer),
        });
      },
      (err) => {
        if (latestRequest.current !== current) return;
        if (err?.name === 'CanceledError') return;
        console.error(err);
        setRequest({ status: 'error', companyValue: null });
      },
    );
    return () => {
      latestRequest.current = null;
    };
  }, [idCompany, wagePerHour, reset]);

  const startEditing = () => {
    setInput(formatMissionCoefficient(request.companyValue) ?? '');
    setIsEditing(true);
  };

  const parsed = isEditing
    ? parseMissionCoefficient(input)
    : { value: null, error: null };
  const isInvalid = Boolean(parsed.error);
  const override =
    !isInvalid && parsed.value !== null && parsed.value !== request.companyValue
      ? parsed.value
      : null;

  return {
    ...request,
    isEditing,
    input,
    setInput,
    startEditing,
    reset,
    isInvalid,
    override,
  };
};

export default useMissionCoefficientField;
